import React from "react";
import PropTypes from "prop-types";
import ControlKpiTile from "../ControlKpiTile";
import { humanEUR, pct, unassignedSeverity } from "./ownershipFormat";

const material = (c) => Math.round(Number(c) || 0) >= 1;

// unownedRemediation maps the unowned split to the RIGHT action + deep-link, so the CTA never tells
// a CFO to "assign an owner" for spend whose real fix is "add a source". When both problems exist
// we keep it generic ("Review unowned") and land on the allocation page.
function unownedRemediation(noOwnerCost, uncoveredCost, uPct) {
  const noOwner = material(noOwnerCost);
  const uncovered = material(uncoveredCost);
  if (noOwner && uncovered) {
    return {
      why: `${humanEUR(noOwnerCost)} no owner · ${humanEUR(uncoveredCost)} not captured`,
      cta: "Review unowned →",
      href: "/allocation",
    };
  }
  if (uncovered) {
    return { why: `${pct(uPct)} not captured by a source`, cta: "Add source →", href: "/allocation?focus=uncovered" };
  }
  return { why: `${pct(uPct)} of total spend`, cta: "Assign owner →", href: "/allocation?focus=no-owner" };
}

// OwnershipKpis is the quantitative anchor, built from the SAME ControlKpiTile the Financial
// Control page uses. The Unowned tile is the canonical home for the gap AND its remediation; the
// Concentration tile answers the "who owns" half (largest owner's share — a concentration risk in
// its own right) instead of a bare owner count.
const OwnershipKpis = ({ total, ownedCost, unowned = {}, unassignedPct, ownerCount, topOwner = null, coverageSpark = [] }) => {
  const ownedPct = total > 0 ? ownedCost / total : 0;
  const unassignedCost = Number(unowned.cost) || 0;
  const uPct = unassignedPct != null ? unassignedPct : total > 0 ? unassignedCost / total : 0;
  const unassignedZero = !material(unassignedCost);
  const uTone = unassignedSeverity(unassignedCost, uPct);
  const rem = unassignedZero
    ? { why: "all spend owned", cta: undefined, href: "/allocation" }
    : unownedRemediation(unowned.noOwner && unowned.noOwner.cost, unowned.uncovered && unowned.uncovered.cost, uPct);

  const tiles = [
    {
      key: "total",
      label: "Total spend",
      value: humanEUR(total),
      why: `across ${ownerCount} owner${ownerCount === 1 ? "" : "s"}`,
      tone: "neutral",
      href: "/cost-analyzer",
    },
    { key: "owned", label: "Owned", value: pct(ownedPct), why: humanEUR(ownedCost), tone: "ok", href: "/allocation", spark: coverageSpark, sparkGoodDirection: "up" },
    {
      key: "unowned",
      label: "Unowned",
      value: humanEUR(unassignedCost),
      why: rem.why,
      tone: uTone,
      href: rem.href,
      muted: unassignedZero,
      info: "Spend with no Organisation owner + spend not captured by any source, over the last 30 days. Click to remediate on the allocation page.",
      cta: rem.cta,
    },
    {
      key: "concentration",
      label: "Concentration",
      value: topOwner ? pct(topOwner.share) : "—",
      why: topOwner ? topOwner.name : undefined,
      tone: topOwner && topOwner.share >= 0.6 ? "attention" : "neutral",
      href: "/allocation",
      info: "Share of total spend owned by the largest single owner. High concentration is itself an accountability risk.",
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {tiles.map((t) => (
        <ControlKpiTile key={t.key} tile={t} />
      ))}
    </div>
  );
};

OwnershipKpis.propTypes = {
  total: PropTypes.number.isRequired,
  ownedCost: PropTypes.number.isRequired,
  unowned: PropTypes.shape({
    cost: PropTypes.number,
    pct: PropTypes.number,
    noOwner: PropTypes.shape({ cost: PropTypes.number, count: PropTypes.number }),
    uncovered: PropTypes.shape({ cost: PropTypes.number, count: PropTypes.number }),
  }),
  unassignedPct: PropTypes.number,
  ownerCount: PropTypes.number.isRequired,
  topOwner: PropTypes.shape({ name: PropTypes.string, share: PropTypes.number }),
  coverageSpark: PropTypes.arrayOf(PropTypes.number),
};

export default OwnershipKpis;
